import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { supabaseMarketData, isMarketDataConfigured } from '@/lib/supabase-market-data'
import { captureError } from '@/lib/sentry'
import { fetchAllRows } from '@/lib/supabase-paginate'
import type { TimePeriod } from '@/types/dashboard'
import { periodToDate, previousPeriodDates } from '@/types/dashboard'

export interface PlatformActivity {
  totalTokens: number
  tokensPeriod: number
  launchRateTrend: number // % change vs previous period
  graduationRate: number // graduated / total * 100
  graduatedCount: number
  avgTimeToGraduationHours: number
  totalTradingVolumeSOL: number
  tradingVolumePeriodSOL: number
  activeBondingCurves: number
}

export function usePlatformActivity(period: TimePeriod = '7d') {
  return useQuery({
    queryKey: ['platform-activity', period],
    queryFn: async (): Promise<PlatformActivity> => {
      const periodStart = periodToDate(period)
      const prevPeriod = previousPeriodDates(period) // null for 'all'

      // 1. Token launch data
      const { data: allTokens } = await supabase
        .from('launched_tokens')
        .select('created_at, mint_address')

      const totalTokens = allTokens?.length || 0
      let tokensPeriod = 0
      let tokensPrevPeriod = 0
      const tokenMints = new Set<string>()

      allTokens?.forEach((t) => {
        if (t.mint_address) tokenMints.add(t.mint_address)
        const created = new Date(t.created_at)
        if (created >= periodStart) tokensPeriod++
        if (prevPeriod && created >= prevPeriod.start && created < prevPeriod.end) tokensPrevPeriod++
      })

      const launchRateTrend = prevPeriod
        ? (tokensPrevPeriod > 0
            ? ((tokensPeriod - tokensPrevPeriod) / tokensPrevPeriod) * 100
            : tokensPeriod > 0 ? 100 : 0)
        : 0 // no trend for 'all'

      // 2. Graduation data from pool_stats
      const { data: poolStats } = await supabase
        .from('pool_stats')
        .select('mint_address, is_migrated, synced_at')

      const graduatedMints = new Set<string>()
      const graduationTimes: number[] = []

      // Build map of token creation times for graduation duration calc
      const tokenCreatedAt = new Map<string, Date>()
      allTokens?.forEach((t) => {
        if (t.mint_address) tokenCreatedAt.set(t.mint_address, new Date(t.created_at))
      })

      poolStats?.forEach((ps) => {
        if (ps.is_migrated && ps.mint_address) {
          graduatedMints.add(ps.mint_address)
          const launchDate = tokenCreatedAt.get(ps.mint_address)
          if (launchDate && ps.synced_at) {
            const hours = (new Date(ps.synced_at).getTime() - launchDate.getTime()) / (1000 * 60 * 60)
            if (hours > 0) graduationTimes.push(hours)
          }
        }
      })

      const graduatedCount = graduatedMints.size
      const graduationRate = totalTokens > 0 ? (graduatedCount / totalTokens) * 100 : 0
      const avgTimeToGraduationHours = graduationTimes.length > 0
        ? graduationTimes.reduce((a, b) => a + b, 0) / graduationTimes.length
        : 0

      // Active bonding curves: tokens NOT graduated
      const activeBondingCurves = [...tokenMints].filter((m) => !graduatedMints.has(m)).length

      // 3. Trading volume (from market-data, graceful fallback)
      let totalTradingVolumeSOL = 0
      let tradingVolumePeriodSOL = 0

      if (isMarketDataConfigured()) {
        try {
          // Supabase returns max 1000 rows by default; fetchAllRows paginates to get all.
          const { data: trades } = await fetchAllRows(supabaseMarketData!, {
            table: 'trades',
            select: 'quote_amount, created_at',
          })

          trades?.forEach((trade: Record<string, unknown>) => {
            const sol = Number(trade.quote_amount || 0)
            if (sol <= 0) return
            totalTradingVolumeSOL += sol

            const createdAt = trade.created_at as string | undefined
            if (createdAt && new Date(createdAt) >= periodStart) {
              tradingVolumePeriodSOL += sol
            }
          })
        } catch (error) {
          captureError(error instanceof Error ? error : new Error('usePlatformActivity: trading volume fetch failed'), {
            feature: 'admin_dashboard',
            component: 'usePlatformActivity',
          })
        }
      }

      return {
        totalTokens,
        tokensPeriod,
        launchRateTrend,
        graduationRate,
        graduatedCount,
        avgTimeToGraduationHours,
        totalTradingVolumeSOL,
        tradingVolumePeriodSOL,
        activeBondingCurves,
      }
    },
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  })
}
